import { Link } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { IconLucide, useTanstackTable } from '@yakit/ui-components';

import { VDLAgreementButton } from '@VDL/components/VDLAgreementButton';

import { useVDLStore } from '@VDL/store/VDLstore';

import type { ILevel0DataObject } from '@VDL/api/types/ILevel0Data';
import { VDLBaseTable } from '../VDLBaseTable';

const columnHelper = createColumnHelper<ILevel0DataObject>();

const NumericValue = ({ value }: { value: string | number }) => {
  return value != null ? (
    <div className="w-full text-right font-semibold text-accent-foreground/90">
      {Number(value).toLocaleString('ru-RU')}
    </div>
  ) : (
    ''
  );
};

export const VDLLevel0Table = () => {
  const { level0Data, selectedYear, agreementData } = useVDLStore();

  const tableData = level0Data?.data;
  const tableHeaders = level0Data?.headers;

  const baseColumns = [
    columnHelper.accessor('number_pp', {
      header: '',
      cell: (info) => {
        const original = info.row.original;
        const agreementObject = agreementData?.find(
          (statusObject) => statusObject.code === original.code
        );

        return (
          <div className="flex w-full items-center justify-between">
            <Link
              to={`/${original.code}?year=${selectedYear}`}
              className="hover:text-primary flex items-center gap-2"
            >
              <IconLucide
                name={original.icon_name}
                className="h-4 w-4 shrink-0"
              />
              <span className="line-clamp-1">
                {`${original.number_pp} ${original.name}`}
              </span>
            </Link>
            <div className="mt-1 flex">
              <VDLAgreementButton
                agreementObject={agreementObject}
                level0Code={original.code}
              />
            </div>
          </div>
        );
      },
      size: 500
    })
  ];
  const periodColumns = tableHeaders
    ? Object.entries(tableHeaders).map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ([periodKey, headerData]: [string, any]) =>
          columnHelper.group({
            header: headerData.name,
            columns: [
              {
                header: headerData.plan,
                accessorKey: `${periodKey}.plan`,
                cell: (info) => {
                  const value = info.getValue();

                  return <NumericValue value={value} />;
                }
              },
              {
                header: headerData.fact,
                accessorKey: `${periodKey}.fact`,
                cell: (info) => {
                  const value = info.getValue();

                  return <NumericValue value={value} />;
                }
              }
            ]
          })
      )
    : [];
  const tableColumns = [...baseColumns, ...periodColumns];

  const { table } = useTanstackTable({
    data: tableData ?? [],
    columns: tableColumns,
    columnResizeMode: 'onChange'
  });

  return <VDLBaseTable table={table} data={tableData ?? []} fullWidth={true} />;
};
